<template>
  <div id="content">
    <div class="container">
      <div class="row justify-content-md-center">
        <div class="col-md-12">
          <div class="page-header">
            <h1>Privacy Policy</h1>
          </div>
        </div>
      </div>
    </div>

    <div class="container">
      <div class="row justify-content-md-center">
        <div class="col-md-12">
          <h4 id="section-1">1. Who We Are</h4>
          <h5 id="section-1-1">1.1 Ninkashi Services</h5>
          <p>
            Ninkashi is a ticketing and registration platform dedicated to bringing the world together through live experiences.
            Through our platform, we enable independent artists to host events.
            Ninkashi’s products, features and offerings are available (a) online through Ninkashi properties and (b) through webpages,
            application programming interfaces, and subdomains ("Applications").
            (a) and (b) are collectively referred to as “Ninkashi Properties" or our "Services".
          </p>

          <h5 id="section-1-2">1.2 Who's who</h5>
          <p>
            When this Privacy Policy uses the term "Organizer,” we mean event creators using the Services to create events
            for consumers using our Services (a) to consume information about, or attend, events ("Consumers"),
            or (b) for any other reason. Organizers, Consumers and third parties using our Services are all referred to
            in these Terms collectively as "Users", "you" or "your".
            Ninkashi, Inc. is a Delaware corporation with its principal place of business in San Francisco,
            CA 94109, Reg. No. 8233579 (“Ninkashi,” "us," "we" or "our"). If you are resident in the EEA or
            Switzerland, Ninkashi, Inc. is the responsible party with respect to Personal Data (defined below) collected
            through the Services. If you have any questions or concerns at any time, please do not hesitate to contact
            us at the address above or by contacting us at <a href="mailto:privacy@ninkashi.com">privacy@ninkashi.com</a>.
          </p>

          <h4 id="section-2">2. Our Privacy Statement</h4>
          <h5 id="section-2-1">2.1 Application</h5>
          <p>
            This Privacy Policy sets forth our policy with respect to information that can be associated with or
            which relates to a person and/or could be used to identify a person ("Personal Data") that is collected
            from Users on or through the Services. We take the privacy of your Personal Data seriously.
            Because of that, we have created this Privacy Policy. Please read this Privacy Policy as it includes
            important information regarding your Personal Data and other information.
            "Non-Personal Data" as used in this Privacy Policy is therefore any information that does not relate
            to a person and/or cannot be used to identify a person. When you interact with the Services, we may
            collect Non-Personal Data. The limitations and requirements in this Privacy Policy on our collection,
            use, disclosure, transfer and storage/retention of Personal Data do not apply to Non-Personal Data.
          </p>

          <h4 id="section-3">3. Personal Data that we collect</h4>
          <p>
            When you use or interact with us through the Services, we may collect Personal Data.
            Sometimes this will be on our own behalf and other times this will be on behalf of an Organizer using
            our Services to run an event. This is an important distinction for purposes of certain data
            protection laws and is explained in more detail below.
          </p>

          <h5 id="section-3-1">3.1 Information collected from all Users</h5>
          <p>
            Information you provide to us: For all Users, we collect Personal Data when you voluntarily provide
            such information to the Services, such as when you register for access to the Services, contact us with
            inquiries, respond to one of our surveys, or browse or use certain parts of the Services.
            The Personal Data we may collect includes without limitation your name, address, email address,
            and any other information that you choose to provide and/or that enables Users to be personally identified.
            Information we automatically collect: We also automatically collect certain technical data that is sent
            to us from the computer, mobile device and/or browser through which you access the Services ("Automatic Data").
            Automatic Data, includes without limitation, a unique identifier associated with your access device and/or
            browser (including, for example, your Internet Protocol (IP) address) characteristics about
            your access device and/or browser, statistics on your activities on the Services, information about
            how you came to the Services and data collected through Cookies, Pixel Tags, Local Shared Objects,
            Web Storage and other similar technologies. You can find out more information about how we use Cookies
            and other similar tracking technologies in our Cookie Statement. When you register for the Services or
            otherwise submit Personal Data to us, we may associate other Non-Personal Data (including Non-Personal
            Data we collect from third parties) with your Personal Data. At such instance, we will treat any such
            combined data as your Personal Data until such time as it can no longer be associated with you or used to identify you.
          </p>

          <h5 id="section-3-2">3.2 Information collected from Organizers</h5>
          <p>
            If you are an Organizer we will collect additional Personal Data from you.
            Information you provide to us: In some cases, we may collect your credit card information
            (e.g., your credit card number and expiration date, billing address), some of which may constitute Personal Data,
            to secure certain payments. In addition, if you use our payment processing services, we will collect
            financial information from you (e.g., your bank account information or an address to send checks) as
            necessary to facilitate payments and information required for tax purposes
            (e.g., your taxpayer identification number). Information we obtain from other sources:
            We may also collect or receive Personal Data including your name, email address and other contact
            information from third party sources, such as third party websites and marketing partners, your bank, our
            payment processing partners and credit reporting agencies.
          </p>

          <h5 id="section-3-3">3.3 Information collected from Consumers</h5>
          <p>
            If you are a Consumer, we will collect additional Personal Data from you, sometimes for our own purposes
            and other times on behalf of an Organizer. (See <router-link to="/privacy#section-16" class="text-primary" rel="nofollow">Section 16</router-link>
            below for more information.)  Information you
            provide via Ninkashi Properties or Applications: If you register for a paid event, you will provide financial
            information (e.g., your credit card number and expiration date, billing address, etc.) some of which may
            constitute Personal Data. In addition, Organizers can set up event registration pages to collect virtually any
            information from Consumers in connection with registration for an Organizer's event listed on the Services.
            Ninkashi does not control an Organizer's registration process nor the Personal Data that they collect.
            When you register for, or otherwise provide information to Ninkashi in conjunction with an Organizer event or activity,
            whether that information is yours or a third party’s, in connection with a purchase,
            registration, or transfer, that Organizer will receive and may use the information you provide.
            Please see <router-link to="/privacy#section-5-5" class="text-primary" rel="nofollow">Section 5.5</router-link> below, titled
            “How we disclose and transfer your Personal Data: Organizers” for more information about an Organizer’s
            receipt and use of your Personal Data. Information we obtain from other sources: We may also collect or receive
            Personal Data from third party sources, such as Organizers, other Consumers, social media or other third
            party integrations, your credit card issuing bank, our payment processing partners or other third parties.
          </p>

          <h4 id="section-4">4. How We Use Your Personal Data</h4>
          <p>
            We collect and use the Personal Data we collect in a manner that is consistent with this Privacy Policy,
            and applicable privacy laws. We may use the Personal Data as follows:
          </p>

          <h5 id="section-4-1">4.1 Specific reason</h5>
          <p>
            If you provide Personal Data for a certain purpose, we may use the Personal Data in connection with the
            purpose for which it was provided. For instance, if you contact us by email, we will use the Personal Data
            you provide to answer your question or resolve your problem and will respond to the email address
            from which the contact came.
          </p>

          <h5 id="section-4-2">4.2 Access and use</h5>
          <p>
            If you provide Personal Data to obtain access to or use of the Services or any functionality thereof,
            we will use your Personal Data to provide you with access to or use of the Services or functionality
            and to analyze your use of such Services or functionality. For instance, if you supply Personal Data
            relating to your identity or qualifications to use certain portions of the Services, we will use that
            information to make a decision as to granting you access to use such Services and to assess your ongoing
            qualification to use such Services.

          <h5 id="section-4-3">4.3 Internal business purposes</h5>
          <p>
            We may use your Personal Data for internal business purposes, including without limitation, to help us
            improve the content and functionality of the Services, to better understand our Users, to improve the Services,
            to protect against, identify or address wrongdoing, to enforce our Terms of Service, to manage your account
            and provide you with customer service, and to generally manage the Services and our business.
          </p>

          <h5 id="section-4-4">4.4 Ninkashi marketing</h5>
          <p>
            We may use your Personal Data for our marketing and advertising purposes, including (without limitation) by email,
            SMS marketing, display media, and targeting other devices (such as tablets, mobile devices and televisions).
            We do this in order to inform you about services or events we believe might be of interest to you, develop
            promotional or marketing materials and display Ninkashi or event-related content and advertising on or off
            the Services that we believe might be of interest to you. We may also do this on behalf of an Organizer,
            for example where your prior interactions with an Organizer suggest you may be interested in a particular
            type of event. See "Opt Out from Electronic Communications" below on how to opt out of various Ninkashi marketing communications.
            You may see advertisements for our Services (or our Organizer’s events) on third party websites,
            including on social media platforms. Where you see an advertisement on a third party website or social
            media platform, this may be because we engaged the third party or social media platform to show this
            advertisement to our Users, or to others who have similar attributes to our Users. In some cases, this
            involves sharing your email address or other contact details with the third party or social media platform
            so that they can identify you as one of our Users, or identify other individuals with similar attributes to
            you in order to show them advertisements for our Services (or for our Organizer’s events). If you no longer
            want your Personal Data to be used for these purposes please contact us at <a href="mailto:privacy@ninkashi.com">privacy@ninkashi.com</a>.
            For more information about how we market on, and interact with social media sites, please refer to
            <router-link to="/privacy#section-5-6" class="text-primary" rel="nofollow">Section 5.6</router-link>.
          </p>

          <h5 id="section-4-5">4.5 Organizer emails</h5>
          <p>
            We allow Organizers to use our email tools to contact Consumers for their current and past events, so you
            may receive emails from our system that originate with such Organizers and that we send on their behalf.
            If you registered for an event on the Services, your email address is available to that Organizer. However,
            Organizers may also import the email addresses they have from external sources and send communications through
            the Services to those email addresses, and we will deliver those communications to those email addresses on
            the Organizer's behalf. The Organizer and not Ninkashi is responsible for sending these emails. See "Opt Out
            from Electronic Communications" below on how to opt out of Organizer-initiated communications.
          </p>

          <h5 id="section-4-6">4.6 Use of interest-based data</h5>
          <p>
            We sometimes make inferences about the type of events or activities you may be interested in. We may use
            these inferences to help target advertising or customize recommendations to you, including on behalf of Organizers.
            We may do this on an aggregated or generalized basis. For instance, we may determine that our users who
            attend many comedy events also often attend or show interest in endurance-related events or content.
            We may direct (or assist in directing) content or recommendations related to both comedy and endurance to those Users.
          </p>

          <h5 id="section-4-7">4.7 Other purposes</h5>
          <p>
            If we intend to use any Personal Data in any manner that is not consistent with this Privacy Policy,
            you will be informed of such anticipated use prior to or at the time the Personal Data is collected or
            we will obtain your consent subsequent to such collection but prior to such use.
          </p>

          <h5 id="section-4-8">4.8 Aggregated Personal Data</h5>
          <p>
            In an ongoing effort to understand and serve our Users better, we often conduct research on our customer
            demographics, interests and behavior based on Personal Data and other information that we have collected.
            This research is typically conducted on an aggregate basis only that does not identify you. Once Personal
            Data is in an aggregated form, for purposes of this Privacy Policy, it becomes Non-Personal Data.
          </p>

          <h4 id="section-5">5. How We Disclose and Transfer Your Personal Data</h4>
          <h5 id="section-5-1">5.1 Background</h5>
          <p>
            We are not in the business of selling your Personal Data. We consider this information to be a vital part
            of our relationship with you. Therefore, we will not sell your Personal Data to third parties,
            including third party advertisers. There are certain circumstances in which we may disclose, transfer or
            share your Personal Data with certain third parties without further notice to you, as set forth in this Privacy Policy.
          </p>

          <h5 id="section-5-2">5.2 Business transfers</h5>
          <p>
            As we develop our business, we might sell or buy businesses or assets. In the event of a corporate sale,
            merger, reorganization, dissolution or similar event, Personal Data may be part of the transferred assets.
            We may also disclose your Personal Data in the course of due diligence for such an event. You acknowledge
            and agree that any successor to or acquirer of Ninkashi (or its assets) will continue to have the right to
            use your Personal Data and other information in accordance with the terms of this Privacy Policy.
          </p>

          <h5 id="section-5-3">5.3 Parent companies, subsidiaries, and affiliates</h5>
          <p>
            We may also share your Personal Data with our parent companies, subsidiaries, and/or affiliates for purposes
            consistent with this Privacy Policy. Our parent companies, subsidiaries, and affiliates will be bound to
            maintain that Personal Data in accordance with this Privacy Policy.
          </p>

          <h5 id="section-5-4">5.4 Agents, consultants, and service providers</h5>
          <p>
            We may share your Personal Data with our contractors and service providers who process Personal Data on
            behalf of Ninkashi to perform certain business-related functions. These companies include our marketing
            agencies, online advertising providers, data enhancement and data services providers, database service
            providers, backup and disaster recovery service providers, email service providers, payment processing
            partners, customer support, tech support, hosting companies, and others. When we engage another company
            to perform such functions, we may provide them with information, including Personal Data, in connection with
            their performance of such functions.
          </p>

          <h5 id="section-5-5">5.5 Organizers</h5>
          <p>
            In addition, when you register for an event, sign up for communications, enter a contest, or otherwise
            input your Personal Data (such as through a web form) to communicate with an Organizer or participate in
            an Organizer event, that Organizer will receive that information. For instance, if you input your name and
            email address into a web form for an Organizer offer, activity or event, the Organizer will receive that
            information. The Organizer may then send you marketing or other communications, which may be subject to its
            own, separate privacy policy. Likewise, if you provide your mobile phone number, you may receive information
            messages related to the service, event, activity or information in which you’ve expressed interest.
            When you purchase tickets to, register for or donate to an event, transfer an event ticket or registration
            to another person, enter a contest, or otherwise input your Personal Data (such as through a web form), or
            otherwise communicate with an Organizer, or participate in or express interest in an Organizer or Organizer
            event or activity, that Organizer will receive the information you provide, including your Personal Data,
            and where a ticket or registration is transferred, the Personal Data of the transferee as well. Other third
            parties that are involved in or on whose behalf an event or activity is promoted, may receive that Personal
            Data as well. For example, as to fundraising pages, we may provide your Personal Data both to the Organizer
            charity of the fundraising page and the Organizer of the event to which the fundraising page is linked.
            In some instances, an Organizer may appoint a third party, which may or may not be affiliated with the Organizer,
            to create an event or fundraising page on its behalf (we call these third parties ("Third Party Organizers").
            For example, and without limitation, a concert venue (the Organizer, in this case) may allow third party
            promoters or production companies (the Third Party Organizers) to create events that will be hosted at
            the Organizer's venue using its Ninkashi account. In that case, we may provide your Personal Data to the
            Third Party Organizers on behalf of the Organizers. The same is true for contests and sweepstakes: we may
            provide your Personal Data to both the Organizer promoting the contest or sweepstakes, and any legal sponsor
            of the contest or sweepstakes (such as a brand sponsor).  We are not responsible for the actions of these
            Organizers, or their Third Party Organizers (or other downstream recipients of your Personal Data), with
            respect to your Personal Data. It is important that you review the applicable policies of the Organizers,
            and if applicable and available, their appointed Third Party Organizers, of an event (and the related
            fundraising page, if applicable) before providing Personal Data or other information in connection with
            that event or related fundraising page. Similarly, if you are a member of an Organizer's organization
            within Ninkashi, your Personal Data will be available to the Organizer and shared with those Third Party
            Organizers granted permission by the Organizer to view all members of the Organizer's organization.
          </p>

          <h5 id="section-5-6">5.6 Facebook, Social Media, and other third party connections</h5>
          <p>
            <b>a. Connecting Your Ninkashi Account to Social Media Services.</b>
            You can connect your Ninkashi account to your accounts on third party services like Facebook, in which
            case we may collect, use, disclose, transfer, and store/retain information relating to your account with
            such third party services in accordance with this Privacy Policy. For example, if you connect with Facebook,
            we store your Facebook ID, first name, last name, email, location, friends list, and profile picture; and
            use them to connect with your Facebook account to provide certain functionality on the Services, like
            recommending events that your Facebook friends are interested in and sharing the events you are interested
            in, or attending, with certain groups of people like your Facebook friends.
          </p>
          <p>
            <b>b. “Liking” or “Following”</b>
            Ninkashi on Social Media. In addition, when you “like” or “follow” us on Facebook, Instagram, Twitter or
            other social media sites (to the extent we provide that capability), we may collect some information from
            you including your name, email address, and any comments or content you post relevant to us. We likewise
            may collect your information if you sign up for one of our promotions or submit information to us through
            social media sites.
          </p>
          <p>
            <b>c. Facebook Plug-Ins and Links on Our Pages.</b>
            Ninkashi’s own website may contain links to Facebook as well, such as through the Facebook “Like” or
            “Share” button or other social plug-ins. When
            you interact with these features and links, your browser will establish a direct link with the Facebook
            servers, and Facebook will receive information about your browser and activity, and may link it to your
            Facebook user account. For more information about how Facebook uses data, please see Facebook’s own policies.
          </p>
          <p>
            <b>d. Additional Facebook Marketing and Connectivity.</b>
            In addition, if you are a member of Facebook (or another social media platform), and you provide Personal
            Data to an Organizer (such as in the ways described in <router-link to="/privacy#section-5-5" class="text-primary" rel="nofollow">Section 5.5</router-link>),
            the Organizer may use that Personal Data to send you advertising and offers through Facebook (or another social media)
            platform, including when you are on Facebook or another social media platform. The Organizer may work with
            us and with third parties to enable this data integration and advertising. Facebook and other social media
            platforms may provide a way to opt out of this type of advertising. Please review their user settings and
            support pages to learn more about how they help you to manage privacy and marketing choices.
          </p>
          <p>
            <b>e. Third party services and integrations.</b>
            Ninkashi may provide you with  opportunities to contract directly with third parties, and/or to integrate
            with third party services or  applications, through our platform. In such instances, we will disclose your
            Personal Data to other entities  in order to fulfill a request by you, or to provide services you have requested.
          </p>

          <h5 id="section-5-7">5.7 Legal requirements</h5>
          <p>
            We may disclose your Personal Data if required to do so by law in order to (for example) respond to a
            subpoena or request from law enforcement, a court or a government agency (including in response to public
            authorities to meet national security or law enforcement requirements), or in the good faith belief that
            such action is necessary to (a) comply with a legal obligation, (b) protect or defend our rights, interests
            or property or that of third parties, (c) prevent or investigate possible wrongdoing in connection with the
            Services, (d) act in urgent circumstances to protect the personal safety of Users of the Services or the public,
            or (e) protect against legal liability.
          </p>

          <h4 id="section-6">6. How We Store Your Personal Data</h4>
          <p>
            We may store Personal Data itself or such information may be stored by third parties to whom we have
            transferred it in accordance with this Privacy Policy. We take what we believe to be reasonable steps
            to protect the Personal Data collected via the Services from loss, misuse, unauthorized use, access,
            inadvertent disclosure, alteration and destruction. However, no network, server, database or Internet
            or email transmission is ever fully secure or error free. Therefore, you should take special care in
            deciding what information you send to us electronically. Please keep this in mind when disclosing any
            Personal Data.
          </p>

          <h4 id="section-7">7. How You Can Access, Update, Correct or Delete Your Personal Data</h4>
          <p>
            Registered and unregistered Users and Consumers may also access, update, correct or delete Personal Data
            and exercise these rights using the contact information below. We will consider and respond to all
            requests in accordance with applicable law.
            If a Consumer initiates a data deletion request, Ninkashi is authorized to delete or anonymize
            Personal Data of the requesting Consumer from the Services even if that means removing its availability
            to the Organizer through the Services. However, if you are a Consumer, you understand that even if Ninkashi
            deletes or anonymizes your Personal Data upon your request or pursuant to this Policy, your Personal
            Data may still be available in the Organizer's own databases if transmitted to the Organizer prior to
            Ninkashi receiving or taking action on any deletion or anonymization activity.
          </p>

          <h4 id="section-8">8. How Long We Retain Your Personal Data</h4>
          <p>
            We may retain your Personal Data as long as you are registered to use the Services.
            You may delete your account by using the contact information below. However, we may retain Personal Data
            for an additional period as is permitted or required under applicable laws. Even if we delete your Personal
            Data, it may persist on backup or archival media for an additional period of time for legal, tax or
            regulatory reasons or for legitimate and lawful business purposes.
          </p>

          <h4 id="section-9">9. Cookies, Pixels Tags, Local Shared Objects, Web Storage, and Similar Technologies</h4>
          <h5 id="section-9-1">9.1 What are cookies?</h5>
          <p>
            Cookies are small data files that are placed on your computer or mobile device when you visit a website.
            Cookies are widely used by website owners to make their websites work or work more efficiently, as well as to
            provide reporting information. Cookies set by the website owner (in this case, Ninkashi) are called "first party cookies".
            Cookies set by parties other than the website owner are called "third party cookies".
            Third party cookies enable third party features or functionality to be provided on or through the
            website (e.g., advertising, interactive content, analytics). The parties that set these third party cookies
            can recognize your computer both when it visits the website in question and also when it visits certain other
            websites.
          </p>

          <h5 id="section-9-2">9.2 Why do we use cookies?</h5>
          <p>
            Ninkashi uses first and third party cookies for several reasons. Some cookies are required for technical
            reasons in order for our Online Properties to operate, and we refer to these as "essential" or
            "strictly necessary" cookies. Other cookies enable us to track and target the interests of our Users to
            enhance the experience on our Online Properties. Third parties serve cookies through our Online Properties
            for advertising, analytics and other purposes. This is described in more detail below.
          </p>
          <p>
            The specific types of first and third party cookies served through our Online Properties and the purposes
            they perform are described under "Essential 1st party cookies" and "Analytics and customization cookies" below.
            (Please note that the specific cookies served may vary depending on the specific Ninkashi Property you visit.)
          </p>

          <h5 id="section-9-3">9.3 Essential 1st party cookies</h5>
          <p>
            These cookies are strictly necessary to provide you with Services and to use some of the Site or Application,
            such as access to secure areas. Who serves these cookies: ninkashi.com and related top level domains.
            How to refuse: Because these cookies are strictly necessary to deliver the Online Properties to you, you cannot refuse them.
          </p>
          <p>
            You may block or delete them by changing your browser settings, as described below under the heading
            "How can I control cookies?", but you may not be able to use every Site or Application features, and in
            some cases the Ninkashi Property simply will not perform.
          </p>

          <h5 id="section-9-4">9.4 Analytics and customization cookies</h5>
          <p>
            These cookies collect information that is used either in identifiable or aggregate form to help us understand
            how our Online Properties are being used, the effectiveness of our marketing campaigns, or to help us customize
            our Online Properties for you. The following third parties use cookies on Online Properties:
          </p>
          <ul>
            <li>
              <a href="https://policies.google.com/technologies/partner-sites?hl=en" target="_blank" class="text-primary"
                rel="nofollow">
                Google Analytics & AMP Client ID
              </a>
            </li>
            <li>
              <a href="https://www.facebook.com/policies/cookies/" target="_blank" class="text-primary" rel="nofollow">
                Facebook
              </a>
            </li>
            <li>
              <a href="https://help.instagram.com/519522125107875" target="_blank" class="text-primary" rel="nofollow">
                Instagram
              </a>
            </li>
            <li>
              <a href="https://twitter.com/privacy?lang=en" target="_blank" class="text-primary" rel="nofollow">
                Twitter
              </a>
            </li>
          </ul>

          <p>
            How to refuse: To refuse these cookies, please follow the instructions below under the heading
            "How can I control cookies?" Alternatively, you can click on the link accompanying the third party cookie
            list above to learn more about how each third party uses cookies and to opt-out.
          </p>

          <h5 id="section-9-5">9.5 What about other tracking technologies, like web beacons?</h5>
          <p>
            Cookies are not the only way to recognize or track visitors to a website.
            Web beacons (sometimes called "tracking pixels" or "clear gifs") are tiny graphics files that contain a
            unique identifier that enable us to recognize when someone has visited our Online Properties or taken an
            action in relation to our Online Properties. We also facilitate our organizers ability to learn more about
            your interest in their event at their request through their use of these tracking pixels on our Online Properties.
            In many instances, these technologies are reliant on cookies to function properly. As a result,
            declining cookies will impair their functioning.
          </p>

          <h5 id="section-9-6">9.6 Does Ninkashi allow for Social Networking Cookies?</h5>
          <p>
            Yes, these cookies are used to enable you to share pages and content that you find interesting, or events
            you might attend, through third party social networking and other websites. These cookies may also be used
            for advertising purposes by those social networks. The best way to refuse these cookies is to follow the
            instructions below under the heading "How can I control cookies?"
          </p>

          <h5 id="section-9-7">9.7 What about targeted advertising?</h5>
          <p>
            We do not offer you 3rd party advertising on Online Properties; however, organizers selling tickets to
            events on Online Properties may use information about your visits to Online Properties and other websites
            to provide relevant advertisements about goods and services in which you may be interested. Organizers may
            also employ technology that is used to measure the effectiveness of advertisements. This can be accomplished
            by them using cookies or web beacons to collect information about your visits to Online Properties and other
            sites in order to provide relevant advertisements about goods and services of potential interest to you.
            The information collected through this process does not enable us or them to identify your name, contact
            details or other personally identifying details unless you choose to provide these.
          </p>

          <h5 id="section-9-8">9.8 How can I control cookies?</h5>
          <p>
            You have the right to decide whether to accept or reject cookies. You can exercise your cookie preferences
            by clicking on the appropriate opt-out links provided in the cookie table above, or you can set or amend
            your web browser controls to accept or refuse cookies. If you choose to reject cookies via your browser,
            you may still use our Online Properties though your access to some functionality may be restricted.
            As the means by which you can refuse cookies through your web browser controls vary from browser-to-browser,
            you should visit your browser's help menu for more information.
          </p>

          <h5 id="section-9-9">9.9 How often will you update this Cookie Statement?</h5>
          <p>
            We may update this Cookie Statement from time to time to reflect, for example, changes to the cookies we
            use or for other operational, legal or regulatory reasons. Please therefore re-visit this Cookie Statement
            regularly to stay informed about our use of cookies and related technologies. The date at the top of this
            Cookie Statement indicates when it was last updated.
          </p>

          <h5 id="section-9-10">9.10 Where can I get further information?</h5>
          <p>
            If you have any questions about our use of cookies or other technologies, please email us at
            <a href="mailto:privacy@ninkashi.com">privacy@ninkashi.com</a>.
          </p>

          <h4 id="section-10">10. Your Choices</h4>
          <p>
            You have several choices available when it comes to your Personal Data:
          </p>

          <h5 id="section-10-1">10.1 Limit the Personal Data you provide</h5>
          <p>
            You can browse the Services without providing any Personal Data (other than Automatic Data to the extent
            it is considered Personal Data under applicable laws) or with limiting the Personal Data you provide.
            If you choose not to provide any Personal Data or limit the Personal Data you provide, you may not be able
            to use certain functionality of the Services. For instance, in order to buy tickets as a Consumer, your
            name and email address will be required by the Organizer.
          </p>

          <h5 id="section-10-2">10.2 Opt out from electronic communications</h5>
          <p>
            (a) Organizer-initiated communications. Organizers may use our email tools to send electronic communications
            to those on their email subscription lists, including Consumers who have registered for their events on the
            Services in the past. Although these electronic communications are sent through our system,
            Ninkashi does not determine the content or the recipients of these electronic communications.
            Organizers are required to use our email tools only in accordance with all applicable laws. Ninkashi provides
            an "Unsubscribe" link on each of these emails, which allows recipients to "opt out" of electronic
            communications from the particular Organizer.
          </p>
          <p>
            (b) Social notifications. If you connect your Facebook account or sign up for other social media integrations
            whose product features include social notifications (i.e., updates on what your friends are doing on the Services),
            you will receive these social notifications. You can manage these social notifications by toggling your
            social settings to private or disconnecting such integration.
          </p>
          <p>
            (c) Transactional or responsive communications. Certain electronic communications from Ninkashi are responsive
            to your requests. For instance, if you are a Consumer, we must email you your ticket or registration on
            behalf of the Organizer when you purchase such ticket or registration. As a further example, if you email
            our customer support department, we will return your email. Notwithstanding any unsubscribe election that
            you have made, you will still receive these transactional or responsive emails. You can stop receiving these
            types of emails only by contacting us. By electing to stop receiving all electronic communications from us
            or through our system, you will no longer receive any updates on events you have created (including payout issues)
            or on events you are registered to attend (including emails with your tickets). We do not recommend that you
            do this unless you plan to no longer use the Services, are not currently registered for an event, are not
            currently organizing an event, and will have no need to receive further communications from us or through our system.
          </p>
          <p>
            (d) Retention. It may take up to forty-eight (48) hours for us to process an unsubscribe request. Even after
            you opt out of all electronic communications, we will retain your Personal Data in accordance with this
            Privacy Policy, however, we will no longer use it to contact you. However, Organizers who have received your
            Personal Data in accordance with this Privacy Policy may still use that Personal Data to contact you in
            accordance with their own privacy policies, but they may not use our system to do so.
          </p>

          <h5 id="section-10-3">10.3 Do Not Track</h5>
          <p>
            We currently do not participate in any "Do Not Track" frameworks that would allow us to respond to signals or
            other mechanisms from you regarding the collection of your Personal Data.
          </p>

          <h4 id="section-11">11. Exclusions</h4>
          <h5 id="section-11-1">11.1 Personal Data provided to others</h5>
          <p>
            This Privacy Policy does not apply to any Personal Data that you provide to another User or visitor through
            the Services or through any other means, including to Organizers on event pages or information posted by you
            to any public areas of the Services.
          </p>

          <h5 id="section-11-2">11.2 Third Party links</h5>
          <p>
            This Privacy Policy applies only to the Services. The Services may contain links to other websites not
            operated or controlled by us (the "Third Party Sites"). The policies and procedures we described here do
            not apply to the Third Party Sites. The links from the Services do not imply that we endorse or have reviewed
            the Third Party Sites. We suggest contacting those sites directly for information on their privacy policies.
          </p>

          <h4 id="section-12">12. Children - Children's Online Privacy Protection Act</h4>
          <p>
            We do not knowingly collect Personal Data from children under the age of thirteen (13).
            If you are under the age of thirteen (13), please do not submit any Personal Data through the Services.
            We encourage parents and legal guardians to monitor their children's Internet usage and to help enforce
            our Privacy Policy by instructing their children never to provide Personal Data through the Services without
            their permission. If you have reason to believe that a child under the age of 13 has provided Personal
            Data to us through the Services, please contact us and we will endeavor to delete that information from our
            databases.
          </p>

          <h4 id="section-13">13. International Privacy Laws</h4>
          <p>
            If you are visiting the Services from outside the United States, please be aware that you are sending information
            (including Personal Data) to the United States where our servers are located. That information may then be
            transferred within the United States or back out of the United States to other countries outside of your
            country of residence, depending on the type of information and how it is stored by us. These countries
            (including the United States) may not necessarily have data protection laws as comprehensive or protective
            as those in your country of residence; however, our collection, storage and use of your Personal Data will
            at all times continue to be governed by this Privacy Policy.
          </p>

          <h4 id="section-14">14. Changes to This Privacy Policy</h4>
          <p>
            The Services and our business may change from time to time. As a result, at times it may be necessary for
            us to make changes to this Privacy Policy. We reserve the right, in our sole discretion, to update or modify
            this Privacy Policy at any time (collectively, "Modifications"). Modifications to this Privacy Policy will
            be posted to the Site with a change to the "Updated" date at the top of this Privacy Policy. In certain
            circumstances Ninkashi may, but need not, provide you with additional notice of such Modifications, such
            as via email or with in-Service notifications. Modifications will be effective thirty (30) days following
            the "Updated" date or such other date as communicated in any other notice to you.  Please review this policy
            periodically, and especially before you provide any Personal Data. This Privacy Policy was updated on the
            date indicated above. Your continued use of the Services following the effectiveness of any Modifications
            to this Privacy Policy constitutes acceptance of those Modifications. If any Modification to this Privacy
            Policy is not acceptable to you, you should cease accessing, browsing and otherwise using the Services.
          </p>

          <h4 id="section-15">15. Dispute Resolution</h4>
          <p>
            If you have a complaint about Ninkashi’s privacy practices you should write to us at <a href="mailto:privacy@ninkashi.com">privacy@ninkashi.com</a>.
            We will take reasonable steps to work with you to attempt to resolve your complaint. Residents of the EEA,
            Switzerland or the UK who believe that their information has not been processed in compliance with the
            Privacy Shield Principles may raise their complaints by contacting us.

          <h4 id="section-16">16. EEA, SWITZERLAND AND UK ONLY</h4>
          <p>
            The EU General Data Protection Regulation (GDPR)
            In May 2018, a new data privacy law known as the EU General Data Protection Regulation (or the "GDPR") went into effect.
            The GDPR requires Ninkashi and Organizers using the Service to provide Users with more information about the
            processing of their Personal Data. Here is what you need to know:
          </p>

          <h5 id="section-16-1">16.1 Legal grounds for processing your</h5>
          <p>
            Personal Data  The GDPR requires us to tell you about the legal ground we're relying on to process any
            Personal Data about you. The legal grounds for us processing your Personal Data for the purposes set out
            in <router-link to="/privacy#section-4" class="text-primary" rel="nofollow">Section 4</router-link> above will typically be because:
            you provided your consent; it is necessary for our contractual relationship;
            the processing is necessary for us to comply with our legal or regulatory obligations; and/or
            the processing is in our legitimate interest as an event organizing and ticketing platform (for example, to
            protect the security and integrity of our systems and to provide you with customer service, etc.).
          </p>

          <h5 id="section-16-2">16.2 Personal Data retention</h5>
          <p>
            We retain your Personal Data for as long as necessary to provide you with our
            Services, or for other important purposes such as complying with legal obligations, resolving disputes, and
            enforcing our agreements.  If you have an account with us, we will typically retain your Personal Data for a
            period of ninety (90) days after you have requested that your account is closed or if it's been inactive for
            seven (7) years.
          </p>

          <h5 id="section-16-3">16.3 Your rights</h5>
          <p>
            Data protection law provides you with rights in respect of Personal Data
            that we hold about you, including the right to request a copy of the Personal Data, request that we rectify,
            restrict or delete your Personal Data, object to profiling and unsubscribe from marketing communications.
            For the most part, you can exercise these rights by logging in and visiting the My Account page or changing the
            "cookie settings" in your browser (see our Cookie Statement for more information). If you can't find what y
            ou're looking for in the My Account page, please contact us using the contact information set out in Section
            15 above. Please note that requests to exercise data protection rights will be assessed by us on a case-by-case basis.
            There may be circumstances where we are not legally required to comply with your request because of the
            laws in your jurisdiction or because of exemptions provided for in data protection legislation. If you
            have a complaint about how we handle your Personal Data, please get in touch with us as set forth in Section
            15 to explain. If you are not happy with how we have attempted to resolve your complaint, you may contact
            the relevant data protection authority.
          </p>

          <h5  id="section-16-4">16.4 Ninkashi as a data controller and a data processor</h5>
          <p>
            EU data protection law makes a distinction between organizations that process Personal Data for their own purposes
            (known as "data controllers") and organizations that process personal data on behalf of other organizations
            (known as "data processors"). If you have a question or complaint about how your Personal Data is handled,
            these should always be directed to the relevant data controller since they are the ones with primary
            responsibility for your Personal Data.  Ninkashi may act as either a data controller or a data processor
            in respect of your Personal Data, depending on the circumstances.  For example, if you create an account
            with us to organize your events, Ninkashi will be a data controller in respect of the Personal Data that
            you provide as part of your account. We will also be a data controller of the Personal Data that we have
            obtained about the use of the Applications or Ninkashi Properties, which could relate to Organizers or Consumers.
            We use this to conduct research and analysis to help better understand and serve Users of the Services as
            well as to improve our platform and provide you with more targeted recommendations about events we think
            may be of interest to you.  However, if you register for an event as a Consumer, we will process your
            Personal Data to help administer that event on behalf of the Organizer (for example, sending confirmation,
            promotional and feedback emails, processing payments, etc.) and to help the Organizer target, and understand
            the success of, their event and event planning (for example, providing event reports, using analytics to
            gain insights into the effectiveness of various sales channels, etc.). In these circumstances, Ninkashi
            merely provides the tools for Organizers; Ninkashi does not decide what Personal Data to request on
            registration forms, nor is it responsible for the continued accuracy of any Personal Data provided.
            Any questions that you may have relating to your Personal Data and your rights under data protection law
            should therefore be directed to the Organizer as the data controller, not to Ninkashi.
          </p>

          <h4 id="section-17">17. CALIFORNIA RESIDENTS ONLY</h4>

          <h5 id="section-17-1">17.1 Ninkashi as a “Business” and a “Service Provider”</h5>
          <p>
            California law makes a distinction between organizations that process Personal Data for their own purposes
            (known as "businesses") and organizations that process Personal Data on behalf of other organizations
            (known as "service providers"). Depending on the circumstances, Ninkashi may act as either a business or
            service provider with respect to your Personal Data. If you have a question or a complaint about how your
            Personal Data is handled, these should always be directed to the relevant business since they are the ones
            with primary responsibility for your Personal Data.  For example, if you create an account with us to
            organize your events, Ninkashi will be a business with respect to the Personal Data that you provide as part
            of your account. We will also be a business as to the Personal Data that we have obtained about the use of
            the Applications or Ninkashi Properties, which could relate to Organizers or Consumers. We use this to conduct
            research and analysis to help better understand and serve Users of the Services as well as to improve our
            platform and provide you with more relevant recommendations about events we think may be of interest to you.
            However, if you register for an event as a Consumer, we will process your Personal Data to help administer
            that event on behalf of the Organizer (for example, sending confirmation, promotional and feedback emails,
            processing payments, etc.) and to help the Organizer target, and understand the success of, their event and
            event planning (for example, providing event reports, using analytics to gain insights into the effectiveness
            of various sales channels, etc.). In these circumstances, Ninkashi merely provides the tools for Organizers;
            Ninkashi does not decide what Personal Data to request on registration forms, nor is it responsible for the
            continued accuracy of any Personal Data provided. Any questions that you may have relating to your Personal
            Data and your rights under California law should therefore be directed to the Organizer as the business,
            not to Ninkashi.
          </p>

          <h5 id="section-17-2">17.2 Summary of Information We Collect</h5>
          <p>
            If you are a California resident, California law requires us to provide you with some additional information
            regarding how we collect, use, and share your “personal information” (as defined in the California Consumer
            Privacy Act (“CCPA”)).  Throughout our Privacy Policy, we describe the specific pieces of personal information
            we collect, the sources of that information, and how we share it. Under the CCPA, we also have to provide
            you with the "categories" of personal information we collect and disclose for “business purposes” (as those
            terms are defined by applicable law). Those categories are identifiers (such as name, address, and email address);
            commercial information (such as transaction data); financial data (such as credit card and other financial
            account information); internet or other network or device activity (such as IP address); geolocation information
            (general location); inference data about you; physical characteristics or description (such as when you
            voluntarily submit a photo); and other information that identifies or can be reasonably associated with you.
            We collect the above categories of personal information from the following sources: (1) directly from you;
            (2) through your use of the Services; (3) agents/service providers; (4) affiliates; (5) third parties such
            as social networks, marketing partners, and credit reporting agencies; (6) organizers; and (7) consumers.
            We or our service providers may collect the above categories of information for the following business or
            commercial purposes:
            Our or our service provider’s operational purposes;
            Auditing consumer interactions on our site (e.g., measuring ad impressions);
            Detecting, protecting against, and prosecuting security incidents and fraudulent or illegal activity;
            Bug detection and error reporting;
            Customizing content that we or our service providers display on the Services (e.g., contextual ads);
            Providing the Services (e.g., account servicing and maintenance, order processing and fulfillment, customer
            service, advertising and marketing, analytics, and communication about the Services);
            Improving our existing Services and developing new services (e.g., by conducting research to develop new
            products or features);
            Other uses that advance our commercial or economic interests, such as third party advertising and
            communicating with you about relevant offers from third party partners;
            Other uses about which we notify you.
          </p>
          <p>
            We may also use the above categories of Personal Data for compliance with applicable laws and regulations,
            and we may combine the information we collect (“aggregate”) or remove pieces of information (“de-identify”)
            to limit or prevent identification of any particular user or device.  We describe our information sharing
            practices in our Privacy Policy. We may share certain categories of personal information with third parties
            for business purposes. For example, we may share any of the categories described above with our parent companies,
            subsidiaries, or affiliates. We may also share identifiers, commercial information, and general location with
            organizers. If you connect your Ninkashi account with social media services, interact with social media
            plugins or links on the Services, or contract or integrate with third parties, we may share identifiers,
            commercial information, internet or other network or device activity, or general location with those social
            media services and third parties.
          </p>

          <h4 id="section-17-3">17.3 Rights</h4>
          <p>
            If you are a California resident, you may have certain rights. California law may permit you to request that we:
            Provide you the categories of Personal Data we have collected or disclosed about you in the last twelve months;
            the categories of sources of such information; the business or commercial purpose for collecting or selling
            your Personal Data; and the categories of third parties with whom we shared Personal Data.
            Provide access to and/or a copy of certain information we hold about you.
            Delete certain information we have about you.
          </p>
          <p>
            You may have the right to receive information about the financial incentives that we offer to you (if any).
            You also have the right to not be discriminated against (as provided for in applicable law) for exercising
            certain of your rights. Certain information may be exempt from such requests under applicable law. For example,
            we need certain types of information so that we can provide the Services to you. If you ask us to delete it,
            you may no longer be able to access or use the Services.  You may also exercise your California consumer
            rights by email. You will be required to verify your identity before we fulfill your request. To do so, you
            will need to log in or provide us with certain account information, such as the full name and email address
            you used to create your account and your most recent event or ticket purchase. You can also designate an
            authorized agent to make a request on your behalf. To do so, you must provide us with written authorization
            for the agent to act on your behalf. You will still need to verify your identity directly with us.
            The CCPA sets forth certain obligations for businesses that “sell” Personal Data. Based on the definition of
            “sell” under the CCPA and under current regulatory guidance, we do not believe we engage in such activity.
            We do share certain information as set forth in <router-link to="/privacy#section-5" class="text-primary" rel="nofollow">Section 5</router-link>
            of the Privacy Policy and allow third parties to collect certain information about your activity, for example
            through cookies, as explained in our Cookie Statement above. For more information on how you can control or
            opt out of these cookies, please see the Cookie Statement above.
          </p>

          <h5 id="section-17-4">17.4 California Shine the Light</h5>
          <p>
            We do not share personal information with third parties for their direct marketing purposes.
          </p>

          <h4 id="section-18">18. NEVADA RESIDENTS ONLY</h4>
          <p>
            Under Nevada law, certain Nevada consumers may opt out of the sale of “personally identifiable information” for
            monetary consideration to a person for that person to license or sell such information to additional persons.
            “Personally identifiable information” includes first and last name, address, email address, phone number,
            Social Security Number, or an identifier that allows a specific person to be contacted either physically or
            online.  We do not engage in such activity; however, if you are a Nevada resident who has purchased or leased
            goods or services from us, you may submit a request to opt out of any potential future sales under Nevada
            law by emailing <a href="mailto:privacy@ninkashi.com">privacy@ninkashi.com</a>. Please note we will take
            reasonable steps to verify your identity and the authenticity of the request. Once verified, we will
            maintain your request in the event our practices change.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PrivacyPolicy',
  data: () => {
    return {

    }
  }
}
</script>
